import { supabase } from "@/lib/supabaseClient";
import { followerAutomationService } from "@/services/followerAutomationService";

export interface MovableProject {
  id: string;
  pipeline_id?: number | null;
  current_stage_id?: number | null;
  [key: string]: unknown;
}

export interface MovedStage {
  id: number;
  name: string;
  pipeline_id: number;
  stage_order: number;
}

/**
 * Moves a project to a stage, in its own pipeline or another one.
 *
 * Pipeline and stage are written in ONE update because
 * `trg_validate_project_pipeline_stage` rejects a stage that does not belong
 * to the row's pipeline, so writing them one after the other always fails.
 *
 * `triggerAutomations: false` is for Undo, which puts the project back where
 * it was: re-running "pipeline entered" automations there would send the
 * same emails and notifications a second time.
 */
export async function moveProjectToPipelineStage({
  project,
  toPipelineId,
  toStageId,
  triggerAutomations = true,
}: {
  project: MovableProject;
  toPipelineId: number;
  toStageId: number;
  triggerAutomations?: boolean;
}): Promise<{ newStage: MovedStage; isPipelineChange: boolean }> {
  const fromPipelineId = project.pipeline_id ?? null;
  const fromStageId = project.current_stage_id ?? null;
  const isPipelineChange = fromPipelineId !== toPipelineId;

  const {
    data: { user },
  } = await supabase.auth.getUser();

  const [{ data: pipelineRows }, { data: stageRows, error: stagesError }] =
    await Promise.all([
      supabase
        .from("pipelines")
        .select("id, name")
        .in("id", [fromPipelineId, toPipelineId].filter((id): id is number => id != null)),
      supabase
        .from("stages")
        .select("id, name, pipeline_id, stage_order")
        .in("id", [fromStageId, toStageId].filter((id): id is number => id != null)),
    ]);
  if (stagesError) throw stagesError;

  const newStage = stageRows?.find((s) => s.id === toStageId) as MovedStage | undefined;
  if (!newStage || newStage.pipeline_id !== toPipelineId) {
    throw new Error("That stage does not belong to the selected pipeline.");
  }
  const oldStage = stageRows?.find((s) => s.id === fromStageId);
  const oldPipeline = pipelineRows?.find((p) => p.id === fromPipelineId);
  const newPipeline = pipelineRows?.find((p) => p.id === toPipelineId);
  const now = new Date().toISOString();

  const { error } = await supabase
    .from("projects")
    .update({
      pipeline_id: toPipelineId,
      current_stage_id: toStageId,
      stage_changed_at: now,
      last_updated_by_user: user?.id,
    })
    .eq("id", project.id);
  if (error) throw error;

  if (isPipelineChange) {
    const { error: pipelineLogError } = await supabase.from("activity_logs").insert({
      project_id: project.id,
      user_id: user?.id,
      action_type: "pipeline_changed",
      details: {
        old_pipeline: { id: fromPipelineId, name: oldPipeline?.name || "Unknown" },
        new_pipeline: { id: toPipelineId, name: newPipeline?.name || "Unknown" },
      },
      timestamp: now,
      related_entity_type: "pipeline",
    });
    if (pipelineLogError) console.error("Error logging pipeline change:", pipelineLogError);
  }

  const { error: stageLogError } = await supabase.from("activity_logs").insert({
    project_id: project.id,
    user_id: user?.id,
    action_type: "stage_changed",
    details: {
      old_stage: { id: fromStageId, name: oldStage?.name || "Unknown" },
      new_stage: { id: toStageId, name: newStage.name },
    },
    timestamp: now,
    related_entity_type: "stage",
  });
  if (stageLogError) console.error("Error logging stage change:", stageLogError);

  document.dispatchEvent(
    new CustomEvent("project-stage-updated", {
      detail: { projectId: project.id, stageId: toStageId, pipelineId: toPipelineId },
    })
  );

  if (triggerAutomations) {
    followerAutomationService
      .onStageChanged(project, fromStageId || 0, toStageId)
      .catch((err) => console.error("Error executing stage change automations:", err));

    if (isPipelineChange) {
      followerAutomationService
        .onPipelineEntered({ ...project, pipeline_id: toPipelineId }, toPipelineId)
        .catch((err) => console.error("Error executing pipeline entered automations:", err));
    }
  }

  return { newStage, isPipelineChange };
}
